import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #activeSwipeRow = null;
  #touchStartX = 0;
  #touchStartY = 0;
  #touchDeltaX = 0;
  #touchTracking = false;
  #touchHorizontal = false;
  /** @type {DocumentFragment | null} */
  #emptyCartRollbackSnapshot = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    if (this.isDrawer) {
      this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
      this.addEventListener('touchmove', this.#onTouchMove, { passive: false });
      this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
      this.addEventListener('touchcancel', this.#onTouchEnd, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    if (this.isDrawer) {
      this.removeEventListener('touchstart', this.#onTouchStart);
      this.removeEventListener('touchmove', this.#onTouchMove);
      this.removeEventListener('touchend', this.#onTouchEnd);
      this.removeEventListener('touchcancel', this.#onTouchEnd);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    this.#clearRemoveConfirmations();

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.#clearRemoveConfirmations();

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const snapshot = document.createDocumentFragment();
      Array.from(this.children).forEach((child) => snapshot.appendChild(child.cloneNode(true)));
      this.#emptyCartRollbackSnapshot = snapshot;

      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      // The cart response can land before the exit animation ends. The morph then reuses
      // this <tr> for the NEXT line (rows are paired by position), so removing it blindly
      // deletes a line that is still in the cart. Only remove it if it is still the row
      // we animated out; otherwise the morph has already reconciled it.
      const removedKey = row.dataset.key;
      const remove = () => {
        if (row.dataset.key === removedKey) {
          row.remove();
        } else {
          row.classList.remove('removing');
          row.style.removeProperty('--row-height');
        }
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  onLineItemRemovePrompt(line) {
    const cartItemRow = this.refs.cartItemRows[line - 1];
    if (!cartItemRow) return;

    const isAlreadyOpen = cartItemRow.classList.contains('cart-items__table-row--remove-confirm');
    this.#clearRemoveConfirmations();

    if (!isAlreadyOpen) {
      cartItemRow.classList.add('cart-items__table-row--remove-confirm');
    }
  }

  onLineItemRemoveCancel(line) {
    const cartItemRow = this.refs.cartItemRows[line - 1];
    if (!cartItemRow) return;
    cartItemRow.classList.remove('cart-items__table-row--remove-confirm');
  }

  #onTouchStart = (event) => {
    if (!this.isDrawer || !event.touches?.length) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const row = target.closest('.cart-items__table-row');
    if (!(row instanceof HTMLElement)) return;

    // Keep native interaction for buttons/links/inputs.
    if (target.closest('button, a, input, textarea, select, label')) return;

    this.#activeSwipeRow = row;
    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartY = event.touches[0].clientY;
    this.#touchDeltaX = 0;
    this.#touchTracking = true;
    this.#touchHorizontal = false;
  };

  #onTouchMove = (event) => {
    if (!this.#touchTracking || !(this.#activeSwipeRow instanceof HTMLElement) || !event.touches?.length) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.#touchStartX;
    const deltaY = touch.clientY - this.#touchStartY;
    this.#touchDeltaX = deltaX;

    if (!this.#touchHorizontal) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.#touchTracking = false;
        this.#activeSwipeRow = null;
        return;
      }
      this.#touchHorizontal = true;
    }

    event.preventDefault();

    const isOpen = this.#activeSwipeRow.classList.contains('cart-items__table-row--remove-confirm');
    const revealOffset = parseFloat(getComputedStyle(this.#activeSwipeRow).getPropertyValue('--cart-remove-reveal-offset')) || 120;
    const baseOffset = isOpen ? -revealOffset : 0;
    const minTranslate = -Math.max(revealOffset + 32, 140);
    const translateX = Math.min(0, Math.max(minTranslate, baseOffset + deltaX));

    this.#activeSwipeRow.style.setProperty('--cart-swipe-translate', `${translateX}px`);
    this.#activeSwipeRow.classList.add('cart-items__table-row--swiping');
  };

  #onTouchEnd = () => {
    if (!(this.#activeSwipeRow instanceof HTMLElement)) {
      this.#touchTracking = false;
      return;
    }

    const revealOffset = parseFloat(getComputedStyle(this.#activeSwipeRow).getPropertyValue('--cart-remove-reveal-offset')) || 120;
    const revealThreshold = Math.max(44, revealOffset * 0.45);
    const closeThreshold = -Math.max(16, revealOffset * 0.2);
    const currentTranslate = parseFloat(this.#activeSwipeRow.style.getPropertyValue('--cart-swipe-translate')) || 0;
    const shouldOpen =
      this.#touchDeltaX < -revealThreshold || currentTranslate < -revealThreshold || this.#activeSwipeRow.classList.contains('cart-items__table-row--remove-confirm');
    const shouldClose = this.#touchDeltaX > 36 || currentTranslate > closeThreshold;

    this.#activeSwipeRow.classList.remove('cart-items__table-row--swiping');
    this.#activeSwipeRow.style.removeProperty('--cart-swipe-translate');

    if (shouldOpen && !shouldClose) {
      this.#clearRemoveConfirmations();
      this.#activeSwipeRow.classList.add('cart-items__table-row--remove-confirm');
    } else {
      this.#activeSwipeRow.classList.remove('cart-items__table-row--remove-confirm');
    }

    this.#activeSwipeRow = null;
    this.#touchTracking = false;
    this.#touchHorizontal = false;
    this.#touchDeltaX = 0;
  };

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#emptyCartRollbackSnapshot = null;
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        if (this.#emptyCartRollbackSnapshot) {
          startViewTransition(() => {
            this.replaceChildren(this.#emptyCartRollbackSnapshot);
            this.#emptyCartRollbackSnapshot = null;
          }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
        }
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    // The drawer's section is the whole HEADER. A full morph re-pairs the header's rows by
    // position, so any markup difference (e.g. the index-only <h1> in a response rendered
    // without sections_url) detaches and re-inserts .header__row — and the open modal
    // drawer inside it falls out of the top layer. Morph only the keyed drawer subtree,
    // exactly as updateQuantity already does.
    const mode = this.isDrawer ? 'hydration' : 'full';

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, mode);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#clearRemoveConfirmations();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode });
    }
  };

  #clearRemoveConfirmations() {
    if (!this.refs.cartItemRows) return;
    this.refs.cartItemRows.forEach((row) => {
      row.classList.remove('cart-items__table-row--remove-confirm', 'cart-items__table-row--swiping');
      row.style.removeProperty('--cart-swipe-translate');
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
